/* eslint-disable @typescript-eslint/no-explicit-any */
import { type AuthStoreState } from "@/types";

import { combineReducers, configureStore } from "@reduxjs/toolkit";

import app from "./reducers/app";
import auth from "./reducers/auth";
import dashboard from "./reducers/dashboard";
import backstage from "./reducers/dataBackstage";
import domain from "./reducers/dataPbx";
import system from "./reducers/dataSystem";
import dial from "./reducers/dial";
import root from "./reducers/root";
import settings from "./reducers/settings";
import ui from "./reducers/ui";

// Types
export interface PersistedState {
  settings?: any;
  auth?: AuthStoreState;
  dashboard?: any;
  pbx?: any;
  _persistVersion?: number;
}

export interface UIPersistedState {
  drawerExpanded?: boolean;
  dialDrawer?: boolean;
}

export type MigrationMap<T> = Record<number, (state: T) => T>;

// Migrations
const LATEST_VERSION = 4;
const migrations: MigrationMap<PersistedState> = {
  2: (state) => ({
    ...state,
    settings: { ...state.settings, logs: { refreshInterval: 0 } },
  }),
  3: (state) => ({
    ...state,
    settings: { ...state.settings, colorFilter: "default" },
  }),
  4: (state) => ({
    ...state,
    settings: {
      ...state.settings,
      logs: { ...state.settings?.logs, expand: false },
    },
  }),
};

const runMigrations = <T>(state: T, storedVersion: number, migrations: MigrationMap<T>): T => {
  let migrated = { ...state };
  const versions = Object.keys(migrations)
    .map(Number)
    .sort((a, b) => a - b);

  for (const version of versions) {
    if (version > storedVersion && version <= LATEST_VERSION) {
      migrated = migrations[version](migrated);
    }
  }

  return migrated;
};

// Load and store data
const loadState = (): PersistedState | undefined => {
  try {
    const raw = localStorage.getItem("sippy");
    if (!raw) return undefined;

    const parsed: PersistedState = JSON.parse(raw);
    const storedVersion = parsed._persistVersion ?? 0;

    // Remove _persistVersion before passing to Redux
    const { _persistVersion, ...rest } = parsed;

    return runMigrations(rest, storedVersion, migrations);
  } catch {
    return undefined;
  }
};

const saveState = (state: RootState): void => {
  const whitelist: (keyof PersistedState)[] = ["settings", "auth", "dashboard", "pbx"];

  const filtered: PersistedState = Object.fromEntries(
    Object.entries(state).filter(([key]) => whitelist.includes(key as keyof PersistedState)),
  ) as PersistedState;

  localStorage.setItem("sippy", JSON.stringify({ ...filtered, _persistVersion: LATEST_VERSION }));
};

// Load and store UI state
const loadUIState = (): UIPersistedState | undefined => {
  try {
    const raw = localStorage.getItem("ui");
    return raw ? (JSON.parse(raw) as UIPersistedState) : undefined;
  } catch {
    return undefined;
  }
};

const saveUIState = (uiState: RootState["ui"]): void => {
  const { drawerExpanded, dialDrawer } = uiState;
  const data: UIPersistedState = { drawerExpanded, dialDrawer };
  localStorage.setItem("ui", JSON.stringify(data));
};

// Reducers
const reducers = combineReducers({
  app,
  ui,
  auth,
  backstage,
  settings,
  dashboard,
  dial,
  domain,
  root,
  system,
});

export type RootState = ReturnType<typeof reducers>;

// Store
const preloadedState: Partial<RootState> = {
  ...(loadState() as any),
  ui: { ...loadUIState() },
};

const store = configureStore({
  reducer: reducers,
  preloadedState,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActionPaths: ["payload.dndEvent"],
      },
    }),
});

// Subscribe to changes
store.subscribe(() => {
  const state = store.getState();
  saveState(state);
  saveUIState(state.ui);
});

export type AppStore = typeof store;
export type StoreState = ReturnType<AppStore["getState"]>;

export default store;
